import { OpenImgContextProvider } from 'openimg/react'
import {
	Links,
	type LinksFunction,
	type LoaderFunctionArgs,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	type UIMatch,
	data,
	useLoaderData,
	useMatches,
	useRouteLoaderData,
} from 'react-router'
import { useFetcher } from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { getLandlordSelfSignupMode } from '#app/services/module-access.server'
import type { Route } from './+types/root'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import ComingSoon from './components/ComingSoon'
import { CookieConsent } from './components/consent/CookieConsent'
import { GeneralErrorBoundary } from './components/error-boundary'
import { Layout as HighLevelLayout } from './components/layout'
import { EpicProgress } from './components/progress-bar'
import { GetStartedDialog } from './components/site/GetStartedDialog'
import { useToast } from './components/toaster'
import { Button } from './components/ui/button'
import { href as iconsHref } from './components/ui/icon'
import { Input } from './components/ui/input'
import { EpicToaster } from './components/ui/sonner'
import { useOptionalTheme, useTheme } from './routes/resources+/theme-switch'
import bricolageGrotesqueStylesheetUrl from './styles/bricolageGrotesqueStylesheet.css?url'
import consentStyleSheetUrl from './styles/consent.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUser, logout } from './utils/auth.server'
import { ClientHintCheck, getHints } from './utils/client-hints'
import {
	type ConsentRecord,
	consentModeBootScript,
	getConsentFromCookieHeader,
} from './utils/consent/consent'
import { sidebarState } from './utils/cookies.server'
import { db } from './utils/db.server'
import { getEnv } from './utils/env.server'
import { pipeHeaders } from './utils/headers.server'
import { honeypot } from './utils/honeypot.server'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc'
import { useNonce } from './utils/nonce-provider'
import { getSession } from './utils/session.server'
import { type Theme, getTheme } from './utils/theme.server'
import { makeTimings, time } from './utils/timing.server'
import { getToast } from './utils/toast.server'
import { truetenant } from './utils/truetenant/truetenant'
import { useOptionalUser } from './utils/user'
export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ as: 'image', href: iconsHref, rel: 'preload' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ href: '/favicon.svg', rel: 'icon', type: 'image/svg+xml' },
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{ rel: 'stylesheet', href: consentStyleSheetUrl },
		{ rel: 'preload', href: bricolageGrotesqueStylesheetUrl, as: 'style' },
		{ rel: 'stylesheet', href: bricolageGrotesqueStylesheetUrl },
		// Preload the Calgary accent font so heading titles (font-calgary) don't
		// flash the fallback before the dialog swaps it in.
		{
			rel: 'preload',
			href: '/fonts/ttf/Calgary_DEMO.ttf',
			as: 'font',
			type: 'font/ttf',
			crossOrigin: 'anonymous',
		} as const,
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{
			title: truetenant.marketing.description.title,
		},
		{
			content: truetenant.marketing.description.meta,
			name: 'description',
		},
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const session = await getSession(request.headers.get('Cookie'))

	const { id: userId, tenantId } =
		(await time(() => getUser(request), {
			desc: 'getUser in root',
			timings,
			type: 'getUser',
		})) ?? {}
	console.log('userId', tenantId)
	const user =
		userId && tenantId
			? await time(
					() =>
						db.query.user.findFirst({
							where: (user, { eq, and }) =>
								and(eq(user.id, userId), eq(user.tenantId, tenantId)),
							with: {
								profile: {
									columns: {
										firstName: true,
										lastName: true,
									},
								},
							},
						}),
					{ desc: 'find user in root', timings, type: 'find user' },
				)
			: null
	if (userId && !user) {
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ redirectTo: '/', request })
	}

	const cookieHeader = request.headers.get('Cookie')
	const cookie = (await sidebarState.parse(cookieHeader)) || {}

	const { headers: toastHeaders, toast } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	// const allProjects = user
	// 	? await db.query.prd.findMany({
	// 			columns: { id: true, name: true },
	// 			where: {
	// 				tenantId: user.tenantId,
	// 			},
	// 		})
	// 	: []
	return data(
		{
			// allProjects,
			ENV: getEnv(),
			honeyProps,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			toast,
			user,
			sidebarOpen: cookie.sidebarOpen,
			/** The visitor's cookie choices; null shows the banner. */
			consent: getConsentFromCookieHeader(cookieHeader),
			isProd: process.env.NODE_ENV === 'production',
			isUnlocked: session.get('unlocked') === true,
			flinksUnlocked: session.get('flinksUnlocked') === true,
			// TT-197: website "Get started" (landlord) books a demo while the
			// public landlord sign-up is paused (Superadmin ▸ Module Access).
			landlordSelfSignupOpen: (await getLandlordSelfSignupMode()) === 'all',
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	env = {},
	nonce,
	theme = 'light',
	consent = null,
}: {
	children: React.ReactNode
	env?: Record<string, string | undefined>
	nonce: string
	theme?: Theme
	consent?: ConsentRecord | null
}) {
	// Always force light theme to prevent system dark mode from affecting the site
	const forcedTheme = 'light'

	return (
		<html
			className={`${forcedTheme} h-full`}
			lang="en"
			style={{ colorScheme: 'light only' }}
		>
			<head>
				<meta charSet="utf-8" />
				{/* The client-hint cookies are Functional: set only with consent. */}
				{consent?.categories.preferences ? (
					<ClientHintCheck nonce={nonce} />
				) : null}
				<Meta />
				<meta name="viewport" content="width=device-width, initial-scale=1.0" />
				{/* Force light color scheme to override system dark mode */}
				<meta name="color-scheme" content="light only" />
				<meta name="theme-color" content="#ffffff" />
				<meta
					content="TrueTenant is a comprehensive tenant eligibility platform that provides landlords with verified tenant screening, document verification, and risk assessment services. Streamline your rental process with our AI-powered tenant verification system."
					name="description"
				/>
				<meta content="website" property="og:type" />
				{/* Google Consent Mode defaults (and a returning visitor's choices),
				    before any Google tag: see app/utils/consent. */}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{ __html: consentModeBootScript(consent) }}
				/>
				{/* Google Tag Manager */}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-T2Z7BGFJ');`,
					}}
				/>
				{/* End Google Tag Manager */}
				{/* Microsoft Clarity and Ahrefs load only after Analytics consent
				    (app/utils/consent/consent.client.ts). */}
				{/* Google tag (gtag.js) */}
				<script
					async
					src="https://www.googletagmanager.com/gtag/js?id=G-WLC5E8BYK6"
					nonce={nonce}
				/>
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `
							window.dataLayer = window.dataLayer || [];
							function gtag(){dataLayer.push(arguments);}
							gtag('js', new Date());
							gtag('config', 'G-WLC5E8BYK6');
						`,
					}}
				/>
				<Links />
			</head>
			<body
				className="bg-background text-foreground"
				style={{ colorScheme: 'light only' }}
			>
				{/* Google Tag Manager (noscript) */}
				<noscript>
					<iframe
						src="https://www.googletagmanager.com/ns.html?id=GTM-T2Z7BGFJ"
						height="0"
						width="0"
						style={{ display: 'none', visibility: 'hidden' }}
						title="Google Tag Manager"
					/>
				</noscript>
				{/* End Google Tag Manager (noscript) */}
				{children}
				<script
					dangerouslySetInnerHTML={{
						__html: `
							window.ENV = ${JSON.stringify(env)};
							
							// Force light mode and prevent system dark mode from affecting the site
							(function() {
								// Override color scheme
								document.documentElement.style.colorScheme = 'light only';
								document.body.style.colorScheme = 'light only';
								
								// Remove any dark class that might be applied
								document.documentElement.classList.remove('dark');
								document.body.classList.remove('dark');
								
								// Force light theme class
								document.documentElement.classList.add('light');
								
								// Override any CSS custom properties that might be affected by system dark mode
								const root = document.documentElement;
								root.style.setProperty('color-scheme', 'light only', 'important');
								
								// Monitor for any changes and revert them
								const observer = new MutationObserver(function(mutations) {
									mutations.forEach(function(mutation) {
										if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
											const target = mutation.target;
											if (target.classList.contains('dark')) {
												target.classList.remove('dark');
												target.classList.add('light');
											}
										}
									});
								});
								
								observer.observe(document.documentElement, {
									attributes: true,
									attributeFilter: ['class']
								});
								observer.observe(document.body, {
									attributes: true,
									attributeFilter: ['class']
								});
							})();
						`,
					}}
					nonce={nonce}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useRouteLoaderData<typeof loader | null>('root')
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document
			nonce={nonce}
			theme={theme}
			env={data?.ENV}
			consent={data?.consent ?? null}
		>
			{children}
		</Document>
	)
}

function FlinksPasswordForm() {
	const fetcher = useFetcher()

	return (
		<div className="flex h-screen w-full flex-col items-center justify-center bg-gray-100 px-4 text-center">
			<h1 className="mb-4 text-4xl font-bold">🔐 Flinks Connect Access</h1>
			<p className="mb-6 max-w-md text-gray-600">
				Enter the password to access the Flinks Connect flow.
			</p>
			<fetcher.Form
				method="POST"
				action="/resources/unlock-flinks"
				className="mt-6 flex flex-col items-center gap-4"
			>
				<div className="flex items-center gap-2">
					<Input
						type="password"
						name="accessKey"
						placeholder="Enter access key"
						aria-label="Access key"
						className="max-w-xs"
					/>
					<Button type="submit" aria-label="Unlock Flinks Connect">
						Unlock
					</Button>
				</div>
				{fetcher.data?.error && (
					<p className="mt-2 text-red-500">{fetcher.data.error}</p>
				)}
			</fetcher.Form>
		</div>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()
	const routes = useMatches()
	const routeType = getRouteType(routes)
	useToast(data.toast)

	// Determine what to render based on production status and access
	const shouldShowComingSoon = () => {
		// If not in production or fully unlocked, show normal content
		if (!data.isProd || data.isUnlocked) {
			return false
		}

		// If on flinks routes and has flinks access, allow them
		const flinksRoutes = ['/flinks-connect', '/flinks-success']
		if (flinksRoutes.includes(data.requestInfo.path) && data.flinksUnlocked) {
			return false
		}

		// Otherwise show coming soon
		return true
	}

	// Check if we should show flinks password form (on flinks-connect without access)
	const shouldShowFlinksPasswordForm = () => {
		return (
			data.isProd &&
			!data.isUnlocked &&
			data.requestInfo.path === '/flinks-connect' &&
			!data.flinksUnlocked
		)
	}

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<>
				<HighLevelLayout
					allProjects={[]}
					routeType={routeType}
					theme={data.requestInfo.userPrefs.theme}
					user={user}
					initialSidebarOpen={data.sidebarOpen}
				>
					{shouldShowFlinksPasswordForm() ? <FlinksPasswordForm /> : <Outlet />}
				</HighLevelLayout>
				<EpicToaster closeButton position="top-center" theme={theme} />
				<EpicProgress />
				<CookieConsent hasRecordOnServer={data.consent !== null} />
				<GetStartedDialog />
			</>
		</OpenImgContextProvider>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

export const ErrorBoundary = GeneralErrorBoundary

export const routeTypes = [
	'internal',
	'marketing',
	'auth',
	'other',
	'site',
] as const

function getRouteType(
	routes: UIMatch<unknown, unknown>[],
): (typeof routeTypes)[number] {
	// The public website (home, landlord, legal) brings its own header and
	// footer: any matched route with `handle.chrome === 'site'` gets no NavBar.
	if (
		routes.some(
			(route) =>
				(route.handle as { chrome?: string } | undefined)?.chrome === 'site',
		)
	) {
		return 'site'
	}
	let match = 'other'
	for (const routeType of routeTypes) {
		const routeId = routes[1].id
		if (routeId.startsWith('routes/_wizard')) {
			match = 'internal'
			break
		}
		if (routeId.startsWith('routes/_auth')) {
			match = 'auth'
			break
		}
		if (routeId === 'routes/demo' || routeId === 'routes/demo-report') {
			match = 'auth'
			break
		}
		if (
			routeId.startsWith('routes/superadmin+') ||
			routeId.startsWith('routes/admin+') ||
			routeId.startsWith('routes/landlord+')
		) {
			match = 'internal'
			break
		}
		if (routeId.startsWith(`routes/_${routeType}+`)) {
			match = routeType
			break
		}
	}

	return match as (typeof routeTypes)[number]
}
